import React, {useState, useEffect, useMemo, useRef } from 'react'
import {Box, Button, Flex, Heading, Text} from '@salesforce/retail-react-app/app/components/shared/ui';
import PropTypes from 'prop-types';
import {FormattedMessage} from 'react-intl';

/* UTILS */
import {findImageGroupBy} from '@salesforce/retail-react-app/app/utils/image-groups-utils'
import isEqual from 'lodash/isEqual';

/* Component */
import StarRating from './star-rating';
import ActualFit from './actual-fit';
import WriteReview from './write-review';

/* Constants */
const VIEW_TYPE = 'small'

/* API Hooks */
import {useDerivedProduct} from '@salesforce/retail-react-app/app/hooks'

import {sendWriteReviewEvent} from '../../../utils/gtm-utils'

const ReviewsHeadline = ({ product, isLoading, reviewFormData, noReviews, configs = {}, onOpen }) => {
    const {variationParams} = useDerivedProduct(product)

    const [isWriteReviewOpenModal, setIsWriteReviewOpenModal] = useState(false);
    const [selectedVariationParams, setSelectedVariationParams] = useState(variationParams)
    const writeReviewButtonRef = useRef(null);

    const totalReviews = product?.c_bvReviewCount;
    const averageRating = product?.c_bvAverageRating?.toString();
    const fitValue = product?.c_bvHowDoesItFitAverageRating;
    const imageGroups = product?.imageGroups;

    const defaultConfigs = {
        styles: {
            container: {
                backgroundColor: {base: 'warmWhite'},
                px: {base: 4, lg: 16},
                py: 8,
                mb: {base: 8}
            },
            writeReviewCTA: {
                variant: {base: 'outline'},
                w: {base: '100%'},
                mt: {base: 8, lg: noReviews ? 6 : 8},
                minH: {base: '48px'},
                backgroundColor: {base: 'warmWhite'},
                maxW: {lg: '250px'}
            }
        },
        showRecommendations: false,
        showTotalReviews: true
    }
    const mergedConfigs = {
        styles: {
            container: {...defaultConfigs.styles.container, ...configs?.styles?.container},
            writeReviewCTA: {...defaultConfigs.styles.writeReviewCTA, ...configs?.styles?.writeReviewCTA}
        },
        showRecommendations: configs?.showRecommendations ?? defaultConfigs.showRecommendations,
        showTotalReviews: configs?.showTotalReviews ?? defaultConfigs.showTotalReviews
    }

    const handleWriteAReview = () => {
        setIsWriteReviewOpenModal(true);
        // GTM Modification
        sendWriteReviewEvent(product)
    }

    const imageGroup = useMemo(
        () =>
            findImageGroupBy(imageGroups, {
                viewType: VIEW_TYPE,
                selectedVariationAttributes: selectedVariationParams
            }),
        [imageGroups, selectedVariationParams]
    )

    useEffect(() => {
        if (!isEqual(selectedVariationParams, variationParams)) {
            setSelectedVariationParams(variationParams);
        }
    }, [variationParams, selectedVariationParams]);

    return (
        <Box {...mergedConfigs.styles.container}>
            <Flex>
                <Box flexBasis="100%">
                    <Heading as="h2"
                        fontSize={{base: "md", lg: "xl"}}
                        lineHeight={{base: "16px", lg: "100%"}}
                        fontWeight="700"
                        textTransform="uppercase"
                        mb={{base: 6, lg: noReviews ? 4 : 8}}>
                            {noReviews && (
                                <FormattedMessage
                                    defaultMessage="Review"
                                    id="pdp_reviews.headline.title.no.reviews"
                                />
                            )}
                            {!noReviews && (
                                <FormattedMessage
                                    defaultMessage="Reviews"
                                    id="pdp_reviews.headline.title"
                                />
                            )}
                    </Heading>
                    {noReviews && (
                        <Text fontSize={{base: "sm"}}
                            lineHeight={{base: "100%"}}>
                            <FormattedMessage
                                defaultMessage="Be the first to review this product."
                                id="pdp_reviews.headline.sub.title.no.reviews"
                            />
                        </Text>
                    )}
                    <Flex flexWrap="wrap"
                        gap={{lg: '120px'}}>
                        {noReviews && (
                            <Button id="pdp-reviews" isLoading={isLoading} {...mergedConfigs.styles.writeReviewCTA}
                                onClick={handleWriteAReview}
                                ref={writeReviewButtonRef}
                                aria-label='Write a review'
                            >
                                <FormattedMessage
                                    defaultMessage="Write a review"
                                    id="pdp_reviews.write.review"
                                />
                            </Button>
                        )}
                        {!noReviews &&
                            <Box flexBasis={{base: "100%", lg: "167px"}}
                                mb={{base: 9, lg: 0}}>
                                <Flex flexWrap={{base: "nowrap", lg: "wrap"}}>
                                    {averageRating &&
                                        <Box flexBasis={{base: "50%", lg: "100%"}}
                                            mb={{base: 0, lg: 8}}
                                            lineHeight="16px">
                                            <Text fontSize="32px"
                                                lineHeight="34px"
                                                textTransform="uppercase"
                                                fontWeight={700} mb={{base: 1, lg: 2}}>
                                                {Number(averageRating).toFixed(1)}
                                            </Text>
                                            <StarRating initialRating={Number(averageRating)} lock={true} styles={{
                                                starBgColor: '#F0EFEB'
                                            }}/>
                                            {mergedConfigs.showTotalReviews &&
                                                <Text fontSize="sm"
                                                    lineHeight="18px" mt={4}
                                                    textDecoration="underline"
                                                    hideFrom="lg"
                                                    onClick={onOpen}
                                                    onKeyDown={(e) => e.key === 'Enter' && onOpen()?.(e)}
                                                    role="button"
                                                    tabIndex={0}
                                                    aria-label={`${totalReviews} reviews`}>
                                                    {totalReviews + ' '}
                                                    <FormattedMessage
                                                            defaultMessage="reviews"
                                                            id="pdp_reviews.headline.reviews"
                                                        />
                                                </Text>
                                            }
                                            <Text fontSize="xs"
                                                lineHeight="15px"
                                                mt={2}
                                                textDecoration={{base: "underline", lg: "none"}}
                                                hideBelow="lg">
                                                <FormattedMessage
                                                    defaultMessage="Based on {totalReviews} reviews"
                                                    id="pdp_reviews.headline.based_on"
                                                        values={{totalReviews}}
                                                />
                                            </Text>
                                        </Box>
                                    }

                                    {mergedConfigs.showRecommendations &&
                                        <Box flexBasis={{base: "50%", lg: "100%"}}>
                                            <Text fontSize="32px"
                                                lineHeight="34px"
                                                textTransform="uppercase"
                                                fontWeight={700}
                                                mb={{base: 1, lg: 2}}>
                                                96%
                                            </Text>
                                            <Text fontSize="xs"
                                                lineHeight="15px"
                                                pr={{base: 0, lg: 4}}>
                                                <FormattedMessage
                                                    defaultMessage="of customers recommend this product"
                                                    id="pdp_reviews.headline.recommended"
                                                />
                                            </Text>
                                        </Box>
                                    }
                                </Flex>
                            </Box>
                        }

                        <Box flexBasis={{base: "100%", lg: "auto"}} flexGrow={1}>
                            {fitValue && !noReviews &&
                                <Box>
                                    <Text hideBelow="lg"
                                        fontSize="sm"
                                        lineHeight="20px"
                                        fontWeight={700}
                                        textTransform="uppercase"
                                        mb={4}>
                                        <FormattedMessage
                                            defaultMessage="Fit"
                                            id="pdp_reviews.description.fit"
                                        />
                                    </Text>
                                    <ActualFit fit={fitValue} />
                                </Box>
                            }

                            {!noReviews && (
                                <Button id="pdp-reviews" isLoading={isLoading} {...mergedConfigs.styles.writeReviewCTA}
                                    onClick={handleWriteAReview}
                                    ref={writeReviewButtonRef}
                                    aria-label='Write a review'
                                >
                                    <FormattedMessage
                                        defaultMessage="Write a review"
                                        id="pdp_reviews.write.review"
                                    />
                                </Button>
                            )}

                            <WriteReview
                                isOpen={isWriteReviewOpenModal}
                                onClose={() => setIsWriteReviewOpenModal(false)}
                                reviewFormData={reviewFormData}
                                images={imageGroup?.images}
                                product={product}
                                writeReviewButtonRef={writeReviewButtonRef}
                            />
                         </Box>
                    </Flex>
                </Box>
            </Flex>
        </Box>
    );
}

ReviewsHeadline.propTypes = {
    product: PropTypes.object,
    isLoading: PropTypes.bool,
    reviewFormData: PropTypes.object,
    configs: PropTypes.object,
    onOpen: PropTypes.func,
    noReviews: PropTypes.bool
}

export default ReviewsHeadline
